import { Paper, Title } from '@mantine/core';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useMantineTheme } from '@mantine/core';

interface OutletRuntimeChartProps {
  periodSummary: Array<{
    period: string;
    state: string;
    hours: number;
  }>;
  bucketSize: 'day' | 'week' | 'month';
  outletName: string;
}

export function OutletRuntimeChart({
  periodSummary,
  bucketSize,
  outletName,
}: OutletRuntimeChartProps) {
  const theme = useMantineTheme();

  const periodMap = new Map<string, Record<string, string | number>>();

  periodSummary.forEach(({ period, state, hours }) => {
    if (!periodMap.has(period)) {
      periodMap.set(period, { period });
    }
    const periodData = periodMap.get(period)!;
    periodData[state] = hours;
  });

  const sortedData = Array.from(periodMap.values()).sort((a, b) =>
    (a.period as string).localeCompare(b.period as string)
  );

  const bucketLabel = bucketSize.charAt(0).toUpperCase() + bucketSize.slice(1);
  const states = Array.from(new Set(periodSummary.map(p => p.state))).sort();

  return (
    <Paper p="md" withBorder>
      <Title order={4} mb="md">
        {outletName} Runtime by {bucketLabel}
      </Title>

      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={sortedData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="period" />
          <YAxis label={{ value: 'Hours', angle: -90, position: 'insideLeft' }} />
          <Tooltip formatter={(value: number) => value.toFixed(1) + ' hrs'} />
          <Legend />
          {states.map((state) => (
            <Bar
              key={state}
              dataKey={state}
              stackId="runtime"
              fill={state === 'ON'
                ? theme.colors.green[6]
                : theme.colors.gray[5]
              }
              name={state}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
}
